import type { Options } from '@growthbook/growthbook';
import {
  FeatureResult,
  GrowthBook,
  InitOptions,
  TrackingCallback,
  useFeatureValue as _useFeatureValue,
  configureCache,
} from '@growthbook/growthbook-react';

import { isProd, IS_DEMO } from 'constants/constants';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { getDeviceId } from 'helpers/deviceId';
import { setUserProperty, trackMixpanelEvent } from 'helpers/mixpanel-init';

import { appFeatureDefaults } from './AppFeatureDefaults';
import { AppFeatureTypes } from './AppFeatureTypes';

export const userABExperiments: Record<string, number> = {};

export const featureInitOptions: InitOptions = {
  streaming: false,
  timeout: 30000,
};

export const trackingCallback: TrackingCallback = (experiment, result) => {
  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.GROWTHBOOK_EXPERIMENT_STARTED, {
    experimentId: experiment.key,
    variationName: result.name,
    variationId: result.variationId,
  });
  userABExperiments[experiment.key] = result.variationId;
  setUserProperty({
    ab_experiments: userABExperiments,
  });
};

export const onFeatureUsage: (featureKey: string, result: FeatureResult) => void = (
  featureKey,
  result
) => {
  if (featureKey === 'sentry_trace_sample_rate') {
    return;
  }
  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.GROWTHBOOK_FEATURE_USED, {
    featureKey,
    value: result.value,
    source: result.source,
  });
};

configureCache({
  // 30 minutes
  staleTTL: 1000 * 60 * 30,

  // 5 minutes
  maxAge: 1000 * 60 * 5,
});

export const getGrowthBookConfig = (): Options => ({
  apiHost: 'https://experiments.delta.exchange',
  clientKey: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
  enableDevMode: !(isProd || IS_DEMO),
  attributes: {
    deviceId: getDeviceId(),
  },
  trackingCallback,
  onFeatureUsage,
});

export const growthBookInstance = new GrowthBook<AppFeatureTypes>(getGrowthBookConfig());

// Initialize GrowthBook
// This is a one-time initialization that will be done when the app starts
growthBookInstance.init(featureInitOptions).then(result => {
  trackMixpanelEvent(MIXPANEL_EVENT_NAMES.GROWTHBOOK_INIT, {
    source: result.source,
    error: result?.error?.message,
  });
});

const reconcileABExperiments = () => {
  const featureKeys = Object.keys(appFeatureDefaults) as Array<keyof AppFeatureTypes>;

  for (let i = 0; i < featureKeys.length; i += 1) {
    const key = featureKeys[i];
    growthBookInstance.getFeatureValue(key, appFeatureDefaults[key]);
  }

  // Unconditionally sync to Mixpanel profile.
  // If no experiments are active, this sets ab_experiments to {},
  // clearing any stale data from previous sessions.
  setUserProperty({
    ab_experiments: userABExperiments,
  });
};

export const setGrowthBookUserID = (userId: number) => {
  growthBookInstance.setAttributes({
    ...growthBookInstance.getAttributes(),
    id: userId,
  });

  // Defer reconciliation off the main thread to avoid blocking
  // login rendering. Evaluates all features to trigger
  // trackingCallback for active experiments, then syncs the
  // canonical ab_experiments to the Mixpanel user profile.
  setTimeout(reconcileABExperiments, 0);
};

export const resetGrowthBookUser = () => {
  growthBookInstance.setAttributes({
    ...growthBookInstance.getAttributes(),
    id: undefined,
  });

  const keys = Object.getOwnPropertyNames(userABExperiments);
  for (let i = 0; i < keys.length; i += 1) {
    delete userABExperiments[keys[i]];
  }
};

export const useGrowthbookFeatureValue = <T = AppFeatureTypes[keyof AppFeatureTypes]>(
  featureKey: keyof AppFeatureTypes
): T => {
  return _useFeatureValue(featureKey, appFeatureDefaults[featureKey]) as T;
};

export const getGrowthBookFeatureValue = (featureKey: keyof AppFeatureTypes) => {
  return growthBookInstance.getFeatureValue(featureKey, appFeatureDefaults[featureKey]);
};

// If the component needs to wait till growthbook is initialised, we can use following:

// <FeaturesReady timeout={500} fallback={<LoadingSpinner/>}>
//   <ComponentThatUsesFeatures/>
// </FeaturesReady>
//      or
// const gb = useGrowthBook();
// if (gb.ready) {
//   <ComponentThatUsesFeatures/>
// }
